import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import styles from "./HeroCarousel.module.scss";

import CatchphraseHero from "images/heros/hero-catchphrase-new.jpg";

const slides = [
    { type: "video", src: "/videos/TheChaseTrailer.mp4", gameId: "the-chase", title: "The Chase" },
    { type: "video", src: "/videos/TenableTrailerv1.mp4", gameId: "tenable", title: "Tenable" },
    { type: "video", src: "/videos/OPCTrailer.mp4", gameId: "one-percent-club", title: "The 1% Club" },
];

const PrevArrow = ({ onClick }) => (
    <button className={`${styles.navArrow} ${styles.prevArrow}`} onClick={onClick} aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </button>
);

const NextArrow = ({ onClick }) => (
    <button className={`${styles.navArrow} ${styles.nextArrow}`} onClick={onClick} aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </button>
);

const VideoSlide = ({ slide, isActive, onEnded }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const navigate = useNavigate();
    const animRef = useRef(null);

    const updateProgress = useCallback(() => {
        const video = videoRef.current;
        if (video && video.duration) {
            setProgress((video.currentTime / video.duration) * 100);
        }
        animRef.current = requestAnimationFrame(updateProgress);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (isActive) {
            video.play().then(() => setIsPlaying(true)).catch(() => {});
            animRef.current = requestAnimationFrame(updateProgress);
        } else {
            video.pause();
            video.currentTime = 0;
            setIsPlaying(false);
            setProgress(0);
            if (animRef.current) cancelAnimationFrame(animRef.current);
        }

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, [isActive, updateProgress]);

    const togglePlay = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play().then(() => setIsPlaying(true));
        } else {
            video.pause();
            setIsPlaying(false);
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        setIsMuted(video.muted);
    };

    const handleProgressClick = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        const bar = e.currentTarget;
        if (!video || !bar) return;
        const rect = bar.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        video.currentTime = percent * video.duration;
    };

    return (
        <div className={styles.videoWrapper}>
            <video
                ref={videoRef}
                src={slide.src}
                muted={isMuted}
                playsInline
                className={styles.videoElement}
                onEnded={onEnded}
            />
            <div className={styles.videoControls}>
                <div className={styles.controlsRow}>
                    <div className={styles.controlsLeft}>
                        <button className={styles.circleBtn} onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                            {isPlaying ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                    </div>
                    <div className={styles.controlsRight}>
                        {slide.gameId && (
                            <button
                                className={styles.buyNowBtn}
                                onClick={(e) => { e.stopPropagation(); navigate(`/shop/${slide.gameId}`); }}
                            >
                                BUY NOW!
                            </button>
                        )}
                        <button className={styles.circleBtn} onClick={toggleMute} aria-label={isMuted ? "Unmute" : "Mute"}>
                            {isMuted ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.progressBar} onClick={handleProgressClick}>
                <div className={styles.progressTrack} />
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
};

const HeroCarousel = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const navigate = useNavigate();
    const sliderRef = useRef(null);

    const handleSlideClick = (slide) => {
        if (slide.gameId) {
            navigate(`/shop/${slide.gameId}`);
        }
    };

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: "18%",
        autoplay: false,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        beforeChange: (current, next) => setActiveIndex(next),
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    centerPadding: "12%",
                }
            },
            {
                breakpoint: 480,
                settings: {
                    centerPadding: "8%",
                }
            }
        ]
    };

    return (
        <section className={styles.heroCarousel}>
            <Slider ref={sliderRef} {...settings} className={styles.slider}>
                {slides.map((slide, i) => (
                    <div key={`hero-slide-${i}`} className={styles.slideWrapper}>
                        <div className={`${styles.slide} ${i === activeIndex ? styles.active : styles.inactive}`}>
                            {slide.type === "video" ? (
                                <VideoSlide slide={slide} isActive={i === activeIndex} onEnded={() => sliderRef.current?.slickNext()} />
                            ) : (
                                <div
                                    className={styles.imageWrapper}
                                    onClick={() => handleSlideClick(slide)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && handleSlideClick(slide)}
                                >
                                    <img
                                        src={slide.src}
                                        alt={slide.title}
                                        className={styles.slideImage}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </Slider>
        </section>
    );
};

export default HeroCarousel;
